/**
 * The recorder's choice of languages (UC-8, light): which are spoken in the recording about to be
 * made, in the order chosen. None named leaves one language to detection; one has everything
 * transcribed in it; two or more have each speaker transcribed in their own. Pure.
 */
import { toggleValue } from '../controls/ToggleChips'
import { RECORDING_LANGUAGES, type RecordingLanguage } from './recording-types'

/** The chip that names no language: one is detected for the whole recording. */
export const DETECT_LANGUAGE = 'auto'

/** A chip of the language field: a language, or the one that leaves it to detection. */
export type RecordingLanguageChip = RecordingLanguage | typeof DETECT_LANGUAGE

/** How many languages one recording names: the server refuses a fifth. */
export const MAX_RECORDING_LANGUAGES = 4

/** The chips in the order the field shows them: detection, then the languages. */
export const RECORDING_LANGUAGE_CHIPS: readonly RecordingLanguageChip[] = [
  DETECT_LANGUAGE,
  ...RECORDING_LANGUAGES,
]

/** Whether the recording names as many languages as it takes. */
export const languagesFull = (languages: readonly RecordingLanguage[]): boolean =>
  languages.length >= MAX_RECORDING_LANGUAGES

/** The chips that are on: the languages named, or detection while none is. */
export const chosenLanguageChips = (
  languages: readonly RecordingLanguage[]
): readonly RecordingLanguageChip[] => (languages.length > 0 ? languages : [DETECT_LANGUAGE])

/** Whether *chip* cannot be picked: one more language than a recording names. */
export const languageChipLocked = (
  languages: readonly RecordingLanguage[],
  chip: RecordingLanguageChip
): boolean => chip !== DETECT_LANGUAGE && !languages.includes(chip) && languagesFull(languages)

/**
 * The languages after a tap on *chip*. Detection stands alone: choosing it clears the languages,
 * and it is on whenever none is named, so it is never tapped off. A language is added at the end
 * or taken away; a fifth is not taken.
 */
export function toggleRecordingLanguage(
  languages: readonly RecordingLanguage[],
  chip: RecordingLanguageChip
): readonly RecordingLanguage[] {
  if (chip === DETECT_LANGUAGE) return []
  if (languageChipLocked(languages, chip)) return languages
  return toggleValue(languages, chip)
}
